'use client';

/**
 * GroupHeaderRow — the one safe click target per pivot group, and the
 * only place `ObeyaCostPlanLinesCard` edits a line (qty / price / spec /
 * remove / room-assign). Own component: `useAffordance` is a hook,
 * group count varies, so this can't be inlined in a `.map()` callback.
 *
 * Selection click-through is GROUP-HEADER-ONLY, not per leaf row. A
 * single line can contribute several placement rows to one expanded
 * group (e.g. Group by Vendor: one line's 4 room placements are 4 leaf
 * rows, all sharing that line's product_sid) — a per-leaf click target
 * keyed by product_sid or vendor_id would put duplicate test ids on
 * screen simultaneously and hit a Playwright strict-mode violation. The
 * group header is always unique per key, so it's the only safe click
 * target: Line mode selects that line's product, Vendor mode selects
 * that vendor. Slot mode has no selectable graph-node type yet, so its
 * header is inert.
 *
 * Qty is editable ONLY when the group is a single, fully-unassigned row
 * (one line, no room placements yet): once any quantity is placed into
 * a room, editing the top-level quantity would silently break the
 * placements-sum-to-quantity invariant — the (separate, harder, unsolved)
 * room-allocation problem this doesn't touch. Price is per-line, not
 * per-placement, so it's always editable in Line mode regardless of
 * allocation state.
 *
 * @see klyde_ipe/docs/mcp-mock-e2e/HANDOVER.md
 * @component_role: Card fragment
 * @component_type: Row renderer
 * @domain: Canvas / Obeya
 * @layer: presentation
 */

import { useState } from 'react';
import { ChevronRight, X } from 'lucide-react';
import { text } from '@/lib/design/tokens';
import { cn } from '@/lib/utils/cn';
import { useAffordance } from '@/lib/stores/total-matrix-store';
import { RoomAssignPicker } from './room-assign-picker';
import { QtyStepper } from './qty-stepper';
import { UNASSIGNED_KEY, money, quoteCell } from './costplan-pivot-shared';
import type { GroupByKey, PivotGroup, VendorColumn, RoomSlotOption } from './costplan-pivot-shared';

const CELL = 'px-3 py-2';

export function GroupHeaderRow({
  g, mode, isOpen, isSelected, onToggle, onSelectLine, onSelectVendor, L, vendorColumns,
  onEditQty, onEditPrice, onEditSpec, onRemove, onEditPlacement, mutating, roomSlots,
}: {
  g: PivotGroup; mode: GroupByKey; isOpen: boolean; isSelected: boolean;
  onToggle: () => void; onSelectLine: (g: PivotGroup) => void; onSelectVendor: (g: PivotGroup) => void;
  L: Record<string, string>; vendorColumns: VendorColumn[];
  onEditQty: (lineId: string, qty: number) => void;
  onEditPrice: (lineId: string, unitPrice: number) => void;
  onEditSpec: (lineId: string, spec: string) => void;
  onRemove: (lineId: string) => void;
  onEditPlacement: (lineId: string, slotSid: string, qty: number) => void;
  mutating: boolean;
  roomSlots: RoomSlotOption[];
}) {
  const lineTestId = useAffordance('canvas.costplan_lines.select_product', { product_sid: g.key });
  const vendorTestId = useAffordance('canvas.costplan_lines.select_vendor_group', { vendor_id: g.key });
  const first = g.rows[0];
  const lineId = first?.line_id ?? null;
  const editQtyTestId = useAffordance('canvas.costplan_lines.edit_qty', { line_id: lineId ?? '' });
  const editPriceTestId = useAffordance('canvas.costplan_lines.edit_price', { line_id: lineId ?? '' });
  const editSpecTestId = useAffordance('canvas.costplan_lines.edit_spec', { line_id: lineId ?? '' });
  const removeTestId = useAffordance('canvas.costplan_lines.remove_line', { line_id: lineId ?? '' });
  const assignTestId = useAffordance('canvas.costplan_lines.assign_room', { line_id: lineId ?? '' });
  const unassigned = g.key === UNASSIGNED_KEY || g.key.startsWith(`${UNASSIGNED_KEY}:`);
  const label = unassigned ? (L.unassigned ?? '') : (g.label || g.key);

  // A group now holds several competing PRODUCTS as soon as it spans
  // more than one line (Line mode groups by the requirement, not the
  // product). The header can only stand in for a single product's own
  // identity/qty/price/spec when the group really is just one line;
  // otherwise those belong on each child row instead (LeafRow's
  // showProductIdentity), not smeared across the header as if `first`
  // spoke for the whole group.
  const singleProduct = g.product_count <= 1;

  const subtitle =
    mode === 'line'
      ? (singleProduct
        ? [first?.product_name, first?.vendor_name].filter(Boolean).join(' · ')
        : `${g.product_count} ${L.col_lines ?? 'products'}`)
      : mode === 'slot'
      ? (first?.slot_count != null ? `${first.slot_count} ${L.col_rooms ?? ''}`.trim() : '')
      : `${g.product_count} ${L.col_lines ?? ''}`;

  const clickable = !unassigned && (mode === 'line' || mode === 'vendor');
  const testId = mode === 'line' ? lineTestId : mode === 'vendor' ? vendorTestId : undefined;
  const onClick = mode === 'line' ? () => onSelectLine(g) : mode === 'vendor' ? () => onSelectVendor(g) : undefined;

  const editableLine = mode === 'line' && !unassigned && !!lineId && singleProduct;
  // Always editable for a real line, regardless of existing room
  // placements — editing the total no longer silently conflicts with an
  // invariant nothing else enforces either; over/under-allocation vs the
  // new total shows up as the Unassigned row going negative (or
  // positive) on next load, same place a buyer already looks.
  const qtyEditable = editableLine;

  // The single line's own remainder/over-allocation state, pulled OUT of
  // the row list and shown as a header-level warning instead of a list
  // item with a fabricated negative dollar value — reconciliation
  // status, not a line of spend. Only meaningful for a genuine
  // single-product group; a multi-product group's several lines each
  // keep their own remainder as an ordinary (product-labeled) leaf row
  // instead — no single header could speak for all of them.
  const remainder = mode === 'line' && singleProduct
    ? g.rows.find((r) => r.slot_sid == null) ?? null
    : null;
  const [specOpen, setSpecOpen] = useState(false);

  return (
    <tr
      className={cn(
        // Grey resting state — the header is structural chrome (a label
        // + group total), not content; tinting it sets it apart from the
        // plain-white leaf rows underneath (see LeafRow's own comment)
        // instead of leaving the distinction to type size alone.
        'cursor-pointer border-b border-border/60 bg-muted/40',
        isSelected ? 'bg-primary/10 shadow-[inset_2px_0_0_hsl(var(--primary))]' : 'hover:bg-muted/60',
      )}
      onClick={onToggle}
    >
      <td className={CELL}>
        <div className="flex items-center gap-1.5">
          <ChevronRight className={cn('w-3 h-3 shrink-0 transition-transform', isOpen && 'rotate-90')} />
          {clickable ? (
            <button
              type="button" data-testid={testId}
              onClick={(e) => { e.stopPropagation(); onClick?.(); }}
              className={cn(text.title, 'hover:text-primary hover:underline text-left')}
            >
              {label}
            </button>
          ) : (
            <span className={text.title}>{label}</span>
          )}
          <span className={text.muted}>({singleProduct ? g.rows.length : g.product_count})</span>
        </div>
        {subtitle && (
          <div className={cn(text.muted, 'pl-[18px] truncate max-w-[280px]')} title={subtitle}>{subtitle}</div>
        )}
        {first?.source_line_label && (
          <div className={cn(text.muted, 'pl-[18px]')}>for: {first.source_line_label}</div>
        )}
        {/* Reconciliation state, not a line of spend — a warning badge
            on the group, never a fabricated negative-value row (see
            `remainder` above). Over-allocated has no action here: the
            fix is reducing placements or raising the line's own
            quantity, neither of which this control does. */}
        {remainder && remainder.over_allocated && (
          <div className="mt-0.5 pl-[18px] flex items-center gap-1 text-warning">
            <span>⚠ Over-allocated by {money(Math.abs(remainder.qty))}</span>
          </div>
        )}
        {remainder && !remainder.over_allocated && remainder.qty > 0 && (
          <div className="mt-0.5 ml-[18px]">
            <RoomAssignPicker
              roomSlots={roomSlots}
              mutating={mutating}
              assignTestId={assignTestId}
              onAssign={(slotSid, qty) => lineId && onEditPlacement(lineId, slotSid, qty)}
              triggerLabel={`${money(remainder.qty)} unplaced · Assign to a room`}
              triggerClassName="text-[10.5px] text-primary hover:underline"
              wrapperClassName="flex items-center gap-1"
            />
          </div>
        )}
        {/* Spec is a ghost disclosure when empty — an always-open empty
            input cost every single-product group a full line for no
            information. Existing spec text stays visible/editable
            inline as before. */}
        {editableLine && (first.spec || specOpen) && (
          <input
            key={`spec-${lineId}-${first.spec ?? ''}`}
            data-testid={editSpecTestId}
            type="text"
            maxLength={200}
            disabled={mutating}
            autoFocus={specOpen && !first.spec}
            defaultValue={first.spec ?? ''}
            placeholder="Spec…"
            onClick={(e) => e.stopPropagation()}
            onBlur={(e) => {
              const v = e.target.value;
              if (lineId && v !== (first.spec ?? '')) onEditSpec(lineId, v);
              if (!v) setSpecOpen(false);
            }}
            className="mt-0.5 ml-[18px] w-[calc(100%-18px)] bg-transparent border-b border-transparent hover:border-border focus:border-primary outline-none text-muted-foreground"
          />
        )}
        {editableLine && !first.spec && !specOpen && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setSpecOpen(true); }}
            className="mt-0.5 ml-[18px] text-[10.5px] text-muted-foreground hover:text-primary hover:underline"
          >
            + Spec
          </button>
        )}
      </td>
      <td className={cn(CELL, 'text-right tabular-nums')} onClick={(e) => e.stopPropagation()}>
        {qtyEditable ? (
          <QtyStepper
            value={g.total_qty ?? 0}
            disabled={mutating}
            testId={editQtyTestId}
            onCommit={(next) => { if (lineId) onEditQty(lineId, next); }}
          />
        ) : mode === 'line' && !singleProduct ? (
          // The requirement's own shared quantity — not a sum across its
          // competing lines (that double/triple-counts one demand as if
          // it were several). qty_mismatch means the competing lines
          // actually disagree on how much is needed — a real
          // discrepancy, surfaced as a warning rather than guessed at.
          g.qty_mismatch
            ? <span className="text-warning font-medium" title="Competing lines disagree on quantity">⚠</span>
            : (g.total_qty?.toLocaleString() ?? '—')
        ) : (
          (g.total_qty ?? 0).toLocaleString()
        )}
      </td>
      <td className={cn(CELL, 'text-right tabular-nums')} onClick={(e) => e.stopPropagation()}>
        {editableLine ? (
          // Total first, bold, larger — it's the figure that actually
          // matters when scanning down the plan; unit price is an input
          // into it, not the headline.
          <div className="flex flex-col items-end gap-0.5">
            <span className="font-semibold text-[1.05em]">{money(g.total_value)}</span>
            {/* The catalogue list price is the best guess BEFORE a real
                quote exists — once one does, it's not a second opinion
                worth showing next to it, it's just stale (see the QUOTED
                cell: the real vendor number). Editable only while
                there's nothing more authoritative to defer to yet. */}
            {!first.reaches_quote && (
              <span className={cn(text.muted, 'flex items-center gap-1 font-normal')}>
                <span className="text-[0.8em]">list</span>
                <input
                  key={`price-${lineId}-${first.unit_price}`}
                  data-testid={editPriceTestId}
                  type="number"
                  step="0.01"
                  disabled={mutating}
                  defaultValue={first.unit_price}
                  onBlur={(e) => {
                    const v = parseFloat(e.target.value);
                    if (lineId && !Number.isNaN(v) && v >= 0 && v !== first.unit_price) onEditPrice(lineId, v);
                  }}
                  className="w-16 text-right tabular-nums bg-transparent border border-transparent hover:border-border focus:border-primary rounded px-1 py-0.5 focus:outline-none text-[0.85em]"
                  title="Catalogue list price"
                />
                each
              </span>
            )}
          </div>
        ) : mode === 'line' && !singleProduct ? (
          // A RANGE, not a fabricated point sum — cheapest competing
          // line's own cost to priciest, never all of them added
          // together as if buying every alternative. Collapses to one
          // number when every alternative happens to cost the same.
          <span className="font-medium">
            {g.value_low === g.value_high
              ? money(g.value_low ?? 0)
              : `${money(g.value_low ?? 0)} – ${money(g.value_high ?? 0)}`}
          </span>
        ) : (
          <span className="font-medium">{money(g.total_value)}</span>
        )}
      </td>
      {vendorColumns.length > 0 ? (
        vendorColumns.map((vc) => {
          const price = g.quotes_by_vendor[vc.vendor_sid];
          return (
            <td key={vc.vendor_sid} className={cn(CELL, 'text-right tabular-nums', price != null && 'text-primary')}>
              {price != null ? money(price) : '—'}
            </td>
          );
        })
      ) : (
        <td className={cn(CELL, 'text-right tabular-nums', g.quote_summary.reaches_quote && 'text-primary')}>
          {quoteCell(g.quote_summary, L)}
        </td>
      )}
      <td className={cn(CELL, 'text-right')} onClick={(e) => e.stopPropagation()}>
        {editableLine && (
          <button
            type="button"
            data-testid={removeTestId}
            disabled={mutating}
            onClick={() => lineId && onRemove(lineId)}
            className="p-1 rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors disabled:opacity-40"
            title="Remove line"
          >
            <X className="w-3 h-3" />
          </button>
        )}
      </td>
    </tr>
  );
}
